/**
 * /admin/platform/members — manage tenant members + their roles.
 *
 * Lists every membership on the current tenant, lets a platform
 * owner or account admin invite new users (with a role on creation),
 * change roles inline, and remove members. Platform-owner role
 * granting is restricted server-side to platform owners; the SPA
 * surfaces the option only when the caller's principal carries
 * that role, and the server is the source of truth.
 *
 * Closes KD-008 (the SPA admin pages that were deferred at S14).
 */

import { type FormEvent, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import {
  adminApi,
  type AdminApiError,
  type InviteIn,
  type Member,
} from "./api";

type Role = "platform_owner" | "account_admin" | "member";

const ROLE_LABEL: Record<Role, string> = {
  platform_owner: "Platform owner",
  account_admin: "Account admin",
  member: "Member",
};

const ROLE_BLURB: Record<Role, string> = {
  platform_owner:
    "Cross-tenant administration: provision tenants, edit platform branding, manage LLM providers, view cross-tenant audit.",
  account_admin:
    "Tenant-internal administration: invite users, change roles, edit tenant branding.",
  member: "Standard user: documents, assessments, chat. No admin surfaces.",
};

function generatePassword(length: number = 16): string {
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => alphabet[b % alphabet.length])
    .join("");
}

export function MembersPage() {
  const { principal } = useAuth();
  const [members, setMembers] = useState<Member[] | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Invite form state.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("member");
  const [invitePassword, setInvitePassword] = useState(generatePassword());
  const [inviteBusy, setInviteBusy] = useState(false);
  const [issuedPassword, setIssuedPassword] = useState<{
    email: string;
    password: string;
  } | null>(null);

  // Per-member action state.
  const [busyUser, setBusyUser] = useState<string | null>(null);

  const callerIsPlatformOwner = principal?.role === "platform_owner";

  async function reload() {
    try {
      const result = await adminApi.listMembers();
      setMembers(result.members);
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Could not load members.");
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setIssuedPassword(null);
    setInviteBusy(true);
    try {
      const body: InviteIn = {
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
        initial_password: invitePassword,
      };
      await adminApi.inviteMember(body);
      setIssuedPassword({ email: body.email, password: invitePassword });
      setInfo(`${body.email} invited as ${ROLE_LABEL[inviteRole]}.`);
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("member");
      setInvitePassword(generatePassword());
      await reload();
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Invite failed.");
    } finally {
      setInviteBusy(false);
    }
  }

  async function onChangeRole(member: Member, next: Role) {
    if (member.role === next) {
      return;
    }
    if (next === "platform_owner" && !callerIsPlatformOwner) {
      setError("Only a platform owner can promote to platform owner.");
      return;
    }
    setError(null);
    setInfo(null);
    setBusyUser(member.user_id);
    try {
      await adminApi.changeMemberRole(member.user_id, next);
      setInfo(`${member.email} is now ${ROLE_LABEL[next]}.`);
      await reload();
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Role change failed.");
    } finally {
      setBusyUser(null);
    }
  }

  async function onRemove(member: Member) {
    if (member.user_id === principal?.user_id) {
      setError("You cannot remove yourself.");
      return;
    }
    const confirmed = window.confirm(
      `Remove ${member.email} from this tenant? They will be logged out and lose access immediately.`,
    );
    if (!confirmed) {
      return;
    }
    setError(null);
    setInfo(null);
    setBusyUser(member.user_id);
    try {
      await adminApi.removeMember(member.user_id);
      setInfo(`${member.email} removed.`);
      await reload();
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Remove failed.");
    } finally {
      setBusyUser(null);
    }
  }

  if (!loaded) {
    return <div className="admin-page">Loading…</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-head admin-page-head-row">
        <div>
          <h2>Members</h2>
          <p className="admin-page-blurb">
            Manage who has access to this tenant and what role they hold.
            Promotions to <strong>platform owner</strong> are restricted to
            existing platform owners; that role grants cross-tenant admin
            powers including LLM configuration, platform branding, and tenant
            provisioning.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setInviteOpen((v) => !v)}
        >
          {inviteOpen ? "Cancel" : "Invite member"}
        </button>
      </header>

      {error !== null && <div className="banner banner-error">{error}</div>}
      {info !== null && <div className="banner banner-info">{info}</div>}

      {issuedPassword !== null && (
        <div className="admin-card">
          <header className="admin-card-head">
            <div>
              <div className="admin-card-title">Temporary password issued</div>
              <div className="admin-card-blurb">
                Hand the password to {issuedPassword.email} via a secure
                channel. They log in once, change the password, and enrol MFA
                on first session. This message is the only place the password
                is shown.
              </div>
            </div>
          </header>
          <div className="admin-card-body">
            <div className="field">
              <label>Email</label>
              <input
                type="text"
                value={issuedPassword.email}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
            <div className="field">
              <label>Temporary password</label>
              <input
                type="text"
                value={issuedPassword.password}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
            <div className="actions">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  void navigator.clipboard.writeText(issuedPassword.password);
                  setInfo("Password copied to clipboard.");
                }}
              >
                Copy password
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setIssuedPassword(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {inviteOpen && (
        <section className="admin-card">
          <header className="admin-card-head">
            <div>
              <div className="admin-card-title">Invite a new member</div>
              <div className="admin-card-blurb">
                Creates a user with a temporary password and a tenant
                membership in the role you choose. The user logs in with the
                temporary password, changes it, and enrols MFA from the SPA.
              </div>
            </div>
          </header>
          <form onSubmit={onInvite} className="admin-card-body">
            <div className="field-row">
              <div className="field">
                <label htmlFor="invite-email">Email</label>
                <input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="invite-role">Role</label>
                <select
                  id="invite-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as Role)}
                >
                  <option value="member">Member</option>
                  <option value="account_admin">Account admin</option>
                  {callerIsPlatformOwner && (
                    <option value="platform_owner">Platform owner</option>
                  )}
                </select>
              </div>
            </div>
            <div className="admin-card-blurb">{ROLE_BLURB[inviteRole]}</div>
            <div className="field">
              <label htmlFor="invite-password">Temporary password</label>
              <div className="field-row">
                <input
                  id="invite-password"
                  type="text"
                  value={invitePassword}
                  onChange={(e) => setInvitePassword(e.target.value)}
                  minLength={12}
                  maxLength={200}
                  required
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setInvitePassword(generatePassword())}
                >
                  Regenerate
                </button>
              </div>
            </div>
            <div className="actions">
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={inviteBusy}
              >
                {inviteBusy ? "Inviting…" : "Send invite"}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setInviteOpen(false)}
                disabled={inviteBusy}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      <h3 className="admin-section-title">Current members</h3>
      {members === null || members.length === 0 ? (
        <div className="empty-row">No members yet.</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Member since</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const isSelf = m.user_id === principal?.user_id;
              return (
                <tr key={m.user_id}>
                  <td>
                    {m.email}
                    {isSelf && <span className="kpi-sub"> (you)</span>}
                  </td>
                  <td>
                    <select
                      value={m.role}
                      disabled={busyUser === m.user_id || isSelf}
                      onChange={(e) =>
                        void onChangeRole(m, e.target.value as Role)
                      }
                    >
                      <option value="member">Member</option>
                      <option value="account_admin">Account admin</option>
                      {(callerIsPlatformOwner || m.role === "platform_owner") && (
                        <option value="platform_owner">Platform owner</option>
                      )}
                    </select>
                  </td>
                  <td>
                    <span className="kpi-sub">
                      {new Date(m.member_since).toLocaleDateString("en-AU")}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={busyUser === m.user_id || isSelf}
                      onClick={() => void onRemove(m)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
